import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import Reveal from "@/components/Reveal";
import { api, formatApiError } from "@/lib/api";
import { CheckCircle2, Loader2, UserPlus, ShieldCheck } from "lucide-react";

const EMPTY = { nome: "", cognome: "", email: "", citta: "" };

export default function Iscrizione() {
  const [form, setForm] = useState(EMPTY);
  const [privacy, setPrivacy] = useState(false);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(null);
  const [error, setError] = useState("");

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!privacy) {
      setError("Per iscriverti devi accettare l'informativa privacy.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/membership", form);
      setDone(data.message);
    } catch (err) {
      setError(formatApiError(err));
    }
    setLoading(false);
  };

  return (
    <div className="container-site py-16 lg:py-24" data-testid="iscrizione-page">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-14 items-start">
        <Reveal>
          <span className="eyebrow">Iscrizione gratuita</span>
          <h1 className="mt-4 text-4xl sm:text-5xl font-extrabold tracking-tight text-brand-ink leading-tight">
            Entra a far parte dell'associazione.
            <span className="text-brand-blue"> Non pagherai mai nulla.</span>
          </h1>
          <p className="mt-6 text-lg text-slate-600 leading-relaxed">
            L'iscrizione è e resterà sempre gratuita. Come socio ricevi aggiornamenti sulle tue
            tutele, accesso prioritario allo sportello di ascolto e l'invito agli incontri
            informativi di Milano e Gallarate.
          </p>
          <ul className="mt-8 space-y-4">
            {[
              "Nessuna quota, nessun costo nascosto: per sempre",
              "I tuoi dati restano riservati e non vengono ceduti a nessuno",
              "Puoi cancellarti quando vuoi, con una semplice email",
            ].map((t) => (
              <li key={t} className="flex gap-3 items-start">
                <ShieldCheck className="w-6 h-6 text-brand-aqua shrink-0 mt-0.5" />
                <span className="text-brand-ink font-medium">{t}</span>
              </li>
            ))}
          </ul>
          <div className="mt-8 rounded-3xl overflow-hidden border-4 border-white shadow-xl shadow-brand-blue/10" data-testid="iscrizione-image">
            <img
              src="https://images.unsplash.com/photo-1529156069898-49953e39b3ac?auto=format&fit=crop&w=1200&q=80"
              alt="Un gruppo di soci dell'associazione che ride insieme"
              loading="lazy"
              className="w-full h-64 object-cover"
            />
          </div>
        </Reveal>

        <Reveal delay={0.15}>
          {done ? (
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="rounded-3xl bg-brand-mist border border-brand-aqua/30 p-10 text-center"
              data-testid="iscrizione-success"
            >
              <CheckCircle2 className="w-16 h-16 text-brand-aqua mx-auto" />
              <h2 className="mt-5 text-2xl font-extrabold text-brand-ink">Iscrizione ricevuta!</h2>
              <p className="mt-3 text-lg text-slate-600">{done}</p>
              <Link to="/guide" className="btn-primary mt-8" data-testid="iscrizione-success-guide-link">
                Intanto, leggi le guide sui tuoi diritti
              </Link>
            </motion.div>
          ) : (
            <form onSubmit={submit} className="rounded-3xl border border-slate-100 bg-white shadow-xl shadow-brand-blue/5 p-8 sm:p-10" data-testid="iscrizione-form">
              <div className="flex items-center gap-3 mb-8">
                <span className="w-11 h-11 rounded-2xl bg-brand-blue text-white flex items-center justify-center">
                  <UserPlus className="w-5 h-5" />
                </span>
                <h2 className="text-2xl font-extrabold text-brand-ink">Modulo di iscrizione</h2>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div>
                  <label htmlFor="is-nome" className="label-field">Nome</label>
                  <input id="is-nome" required value={form.nome} onChange={set("nome")} className="input-field" placeholder="Mario" data-testid="iscrizione-nome-input" />
                </div>
                <div>
                  <label htmlFor="is-cognome" className="label-field">Cognome</label>
                  <input id="is-cognome" required value={form.cognome} onChange={set("cognome")} className="input-field" placeholder="Rossi" data-testid="iscrizione-cognome-input" />
                </div>
                <div>
                  <label htmlFor="is-email" className="label-field">Email</label>
                  <input id="is-email" type="email" required value={form.email} onChange={set("email")} className="input-field" placeholder="mario.rossi@email.it" data-testid="iscrizione-email-input" />
                </div>
                <div>
                  <label htmlFor="is-citta" className="label-field">Città</label>
                  <input id="is-citta" required value={form.citta} onChange={set("citta")} className="input-field" placeholder="Milano" data-testid="iscrizione-citta-input" />
                </div>
              </div>

              <label className="mt-6 flex items-start gap-3 cursor-pointer" data-testid="iscrizione-privacy-label">
                <input
                  type="checkbox"
                  checked={privacy}
                  onChange={(e) => setPrivacy(e.target.checked)}
                  className="mt-1.5 w-5 h-5 accent-brand-blue"
                  data-testid="iscrizione-privacy-checkbox"
                />
                <span className="text-slate-600">
                  Ho letto{" "}
                  <a href="/privacy" target="_blank" rel="noopener noreferrer" className="text-brand-blue font-semibold underline underline-offset-2" data-testid="iscrizione-privacy-link">
                    l'informativa privacy
                  </a>{" "}
                  e acconsento al trattamento dei miei dati per le finalità dell'associazione. *
                </span>
              </label>

              {error && (
                <p className="mt-4 rounded-xl bg-red-50 text-red-700 font-semibold px-4 py-3" role="alert" data-testid="iscrizione-error">
                  {error}
                </p>
              )}

              <button type="submit" disabled={loading} className="btn-primary w-full mt-7 disabled:opacity-60" data-testid="iscrizione-submit-button">
                {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <UserPlus className="w-5 h-5" />}
                {loading ? "Invio in corso…" : "Iscrivimi gratuitamente"}
              </button>
            </form>
          )}
        </Reveal>
      </div>
    </div>
  );
}
